// banking/src/pages/onboarding/corporate/steps/Step3_CompanyDocuments.tsx

import React from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { CorporateFormData } from '../CorporateOnboarding';
import FileUploader from '../components/FileUploader';

interface Step3Props {
  formData: CorporateFormData;
  updateFormData: (data: Partial<CorporateFormData>) => void;
  language: Language;
  errors: Record<string, string>;
  onUploadBusinessRegistration?: (file: File) => Promise<void>;
  onUploadArticlesOfIncorporation?: (file: File) => Promise<void>;
  onUploadBusinessUtilityBill?: (file: File) => Promise<void>;
}

const Step3_CompanyDocuments: React.FC<Step3Props> = ({
  formData,
  updateFormData,
  language,
  errors,
  onUploadBusinessRegistration,
  onUploadArticlesOfIncorporation,
  onUploadBusinessUtilityBill,
}) => {
  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {getTranslation('step3_title', language)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {getTranslation('step3_subtitle', language)}
        </p>
      </div>

      {/* Info box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              {language === 'es'
                ? 'Documentos requeridos'
                : language === 'fr'
                ? 'Documents requis'
                : language === 'de'
                ? 'Erforderliche Dokumente'
                : language === 'it'
                ? 'Documenti richiesti'
                : language === 'pt'
                ? 'Documentos necessários'
                : language === 'nl'
                ? 'Vereiste documenten'
                : language === 'ru'
                ? 'Требуемые документы'
                : language === 'ar'
                ? 'الوثائق المطلوبة'
                : 'Required documents'
              }
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  {language === 'es'
                    ? 'Los 3 documentos son obligatorios'
                    : language === 'fr'
                    ? 'Les 3 documents sont obligatoires'
                    : language === 'de'
                    ? 'Alle 3 Dokumente sind erforderlich'
                    : language === 'it'
                    ? 'Tutti e 3 i documenti sono obbligatori'
                    : language === 'pt'
                    ? 'Os 3 documentos são obrigatórios'
                    : language === 'nl'
                    ? 'Alle 3 documenten zijn verplicht'
                    : language === 'ru'
                    ? 'Все 3 документа обязательны'
                    : language === 'ar'
                    ? 'جميع الوثائق الثلاث مطلوبة'
                    : 'All 3 documents are required'
                  }
                </li>
                <li>
                  {language === 'es'
                    ? 'Formatos aceptados: PDF, JPG, JPEG, PNG'
                    : language === 'fr'
                    ? 'Formats acceptés : PDF, JPG, JPEG, PNG'
                    : language === 'de'
                    ? 'Akzeptierte Formate: PDF, JPG, JPEG, PNG'
                    : language === 'it'
                    ? 'Formati accettati: PDF, JPG, JPEG, PNG'
                    : language === 'pt'
                    ? 'Formatos aceitos: PDF, JPG, JPEG, PNG'
                    : language === 'nl'
                    ? 'Geaccepteerde formaten: PDF, JPG, JPEG, PNG'
                    : language === 'ru'
                    ? 'Допустимые форматы: PDF, JPG, JPEG, PNG'
                    : language === 'ar'
                    ? 'التنسيقات المقبولة: PDF، JPG، JPEG، PNG'
                    : 'Accepted formats: PDF, JPG, JPEG, PNG'
                  }
                </li>
                <li>
                  {language === 'es'
                    ? 'Tamaño máximo: 10MB por archivo'
                    : language === 'fr'
                    ? 'Taille maximale : 10 Mo par fichier'
                    : language === 'de'
                    ? 'Maximale Größe: 10 MB pro Datei'
                    : language === 'it'
                    ? 'Dimensione massima: 10 MB per file'
                    : language === 'pt'
                    ? 'Tamanho máximo: 10 MB por arquivo'
                    : language === 'nl'
                    ? 'Maximale grootte: 10 MB per bestand'
                    : language === 'ru'
                    ? 'Максимальный размер: 10 МБ на файл'
                    : language === 'ar'
                    ? 'الحجم الأقصى: 10 ميجابايت لكل ملف'
                    : 'Maximum size: 10MB per file'
                  }
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Document 1: Business Registration */}
      <div>
        <FileUploader
          value={formData.businessRegistration}
          onChange={(file) => updateFormData({ businessRegistration: file })}
          onFileSelected={onUploadBusinessRegistration}
          language={language}
          error={errors.businessRegistration}
          label={getTranslation('business_registration_label', language)}
          required={true}
          accept=".pdf,.jpg,.jpeg,.png"
          maxSizeMB={10}
        />
      </div>

      {/* Document 2: Articles of Incorporation */}
      <div>
        <FileUploader
          value={formData.articlesOfIncorporation}
          onChange={(file) => updateFormData({ articlesOfIncorporation: file })}
          onFileSelected={onUploadArticlesOfIncorporation}
          language={language}
          error={errors.articlesOfIncorporation}
          label={getTranslation('articles_of_incorporation_label', language)}
          required={true}
          accept=".pdf,.jpg,.jpeg,.png"
          maxSizeMB={10}
        />
      </div>

      {/* Document 3: Business Utility Bill */}
      <div>
        <FileUploader
          value={formData.businessUtilityBill}
          onChange={(file) => updateFormData({ businessUtilityBill: file })}
          onFileSelected={onUploadBusinessUtilityBill}
          language={language}
          error={errors.businessUtilityBill}
          label={getTranslation('business_utility_bill_label', language)}
          required={true}
          accept=".pdf,.jpg,.jpeg,.png"
          maxSizeMB={10}
        />
      </div>

      {/* Progress indicator */}
      {formData.businessRegistration || formData.articlesOfIncorporation || formData.businessUtilityBill ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                {language === 'es'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} de 3 documentos subidos`
                  : language === 'fr'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} sur 3 documents téléchargés`
                  : language === 'de'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} von 3 Dokumenten hochgeladen`
                  : language === 'it'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} di 3 documenti caricati`
                  : language === 'pt'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} de 3 documentos carregados`
                  : language === 'nl'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} van 3 documenten geüpload`
                  : language === 'ru'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} из 3 документов загружено`
                  : language === 'ar'
                  ? `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} من 3 وثائق تم تحميلها`
                  : `${[formData.businessRegistration, formData.articlesOfIncorporation, formData.businessUtilityBill].filter(Boolean).length} of 3 documents uploaded`
                }
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Step3_CompanyDocuments;
